import React from 'react';
import PropTypes from 'prop-types';

import WriteGuard from '../global/ReadOnly/WriteGuard';
import './AccessRequests.css';

const MODE_LABEL = { FULL: 'Full Control', VIEW: 'View Only' };

/** Pill styling per level, including the "nothing yet" case. */
const MODE_CLASS = { FULL: 'ar-pill--full', VIEW: 'ar-pill--view' };

/**
 * The username is what actually identifies the person — display names
 * repeat across companies (both KINGDOMs have a "Master Admin"), so the
 * card shows the name as a heading with the username directly beneath
 * rather than trusting the name on its own.
 */
const nameOf = (r) => r?.targetDisplayName || r?.targetUsername || 'Employee';
const usernameOf = (r) =>
  r?.targetDisplayName && r?.targetUsername ? r.targetUsername : '';

/** Two letters for the avatar: initials where possible, else the name. */
const initialsOf = (r) => {
  const source = (r?.targetDisplayName || r?.targetUsername || '?').trim();
  const words = source.split(/[\s._@-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

/**
 * "just now" / "12 minutes ago" / "3 days ago".
 *
 * How long a request has been sitting there is what tells an owner
 * whether they are holding someone up, so it earns a place on the card.
 * Returns '' for anything unparseable rather than "Invalid Date".
 */
const timeAgo = (value) => {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

/**
 * The bell's Access Requests tab.
 *
 * Three groups, and the middle one is why this is a tab rather than
 * buttons stapled onto an alert:
 *
 *   1. NEEDS YOUR APPROVAL — asks this owner must answer. Full Control
 *      can be granted as View Only instead; that is a counter-offer, not
 *      a refusal, so it sits beside Approve rather than under Decline.
 *   2. ACTIVE ACCESS — every approved grant, each revocable. Without it
 *      saying yes once would be permanent, and no owner would say yes.
 *   3. YOUR REQUESTS — what this company is waiting on, withdrawable.
 *
 * Every action is two-step. These decide who can reach a gameroom's
 * money and customer records; none of them should be one stray click
 * away, and Revoke in particular cuts somebody off mid-shift.
 */
class AccessRequestsPanel extends React.PureComponent {
  state = { confirm: null };

  /** Stage an action. `mode` is set only for the View Only counter-offer. */
  ask = (decision, request, mode) =>
    this.setState({
      confirm: { requestId: request.requestId, decision, mode },
    });

  cancelAsk = () => this.setState({ confirm: null });

  confirmed = () => {
    const { confirm } = this.state;
    if (!confirm) return;
    this.props.decideAccessRequest(confirm.decision, {
      requestId: confirm.requestId,
      mode: confirm.mode,
      // Both ids ride along so the saga can refresh the same two queues
      // it just changed without re-deriving them from the store.
      locationId: this.props.locationId,
      companyId: this.props.companyId,
    });
    this.setState({ confirm: null });
  };

  pendingConfirm = (request) => {
    const { confirm } = this.state;
    return confirm && confirm.requestId === request.requestId ? confirm : null;
  };

  // ---- pieces -------------------------------------------------------------

  renderPill = (mode) => (
    <span className={`ar-pill ${MODE_CLASS[mode] || 'ar-pill--none'}`}>
      {MODE_LABEL[mode] || 'No access'}
    </span>
  );

  /** before -> after. The most important line on a pending card. */
  renderChange = (from, to) => (
    <div className="ar-change">
      {this.renderPill(from)}
      <span className="ar-arrow">&#8594;</span>
      {this.renderPill(to)}
    </div>
  );

  renderWho = (request, granted) => (
    <div className="ar-who">
      <div className={`ar-avatar${granted ? ' ar-avatar--granted' : ''}`}>
        {initialsOf(request)}
      </div>
      <div className="ar-who__text">
        <div className="ar-who__name">{nameOf(request)}</div>
        {usernameOf(request) ? (
          <div className="ar-who__username">{usernameOf(request)}</div>
        ) : null}
      </div>
    </div>
  );

  /**
   * The confirm step. Replaces the action row in place instead of
   * opening a dialog — the card already carries every fact needed to
   * decide, and a modal would only cover it up.
   */
  renderConfirm = (confirm, request) => {
    const who = nameOf(request);
    const where = request.locationName;
    let question;
    let label;

    if (confirm.decision === 'approve') {
      const mode = MODE_LABEL[confirm.mode || request.requestedMode];
      question = `Give ${who} ${mode} at ${where}? They can use it straight away.`;
      label = 'Yes, approve';
    } else if (confirm.decision === 'reject') {
      question = `Decline this request? ${who} keeps whatever access they have now.`;
      label = 'Yes, decline';
    } else if (confirm.decision === 'revoke') {
      question = `Remove ${who}'s access to ${where}? They lose it immediately.`;
      label = 'Yes, revoke';
    } else {
      question = `Withdraw this request? Nothing changes for ${who}.`;
      label = 'Yes, withdraw';
    }

    const danger =
      confirm.decision === 'reject' || confirm.decision === 'revoke';

    return (
      <>
        <div className="ar-line">{question}</div>
        <div className="ar-actions">
          <button
            type="button"
            className="ar-btn ar-btn--ghost"
            onClick={this.cancelAsk}
          >
            Go back
          </button>
          <button
            type="button"
            className={`ar-btn ${danger ? 'ar-btn--danger' : 'ar-btn--primary'}`}
            onClick={this.confirmed}
          >
            {label}
          </button>
        </div>
      </>
    );
  };

  // ---- cards --------------------------------------------------------------

  renderIncoming = (request) => {
    const confirm = this.pendingConfirm(request);
    return (
      <div className="ar-card ar-card--pending" key={request.requestId}>
        {this.renderWho(request)}

        <div className="ar-line">
          <strong>{request.targetCompanyName || 'Another company'}</strong> is
          asking for access at <strong>{request.locationName}</strong>.
        </div>

        {this.renderChange(request.currentMode, request.requestedMode)}

        {confirm ? (
          this.renderConfirm(confirm, request)
        ) : (
          <>
            <div className="ar-meta">
              Asked by {request.requestedByName || 'their owner'}
              {request.requestedByUsername
                ? ` (${request.requestedByUsername})`
                : ''}
              {timeAgo(request.createdAt) ? (
                <>
                  <span className="ar-meta__dot">&#183;</span>
                  {timeAgo(request.createdAt)}
                </>
              ) : null}
            </div>
            <WriteGuard>
              <div className="ar-actions">
                <button
                  type="button"
                  className="ar-btn ar-btn--primary"
                  onClick={() => this.ask('approve', request)}
                >
                  Approve {MODE_LABEL[request.requestedMode]}
                </button>
                {/* Only offered when they asked for more than View Only —
                    otherwise it is the same button twice. */}
                {request.requestedMode === 'FULL' ? (
                  <button
                    type="button"
                    className="ar-btn ar-btn--ghost"
                    onClick={() => this.ask('approve', request, 'VIEW')}
                  >
                    Give View Only
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ar-btn ar-btn--danger ar-btn--wide"
                  onClick={() => this.ask('reject', request)}
                >
                  Decline
                </button>
              </div>
            </WriteGuard>
          </>
        )}
      </div>
    );
  };

  renderGranted = (request) => {
    const confirm = this.pendingConfirm(request);
    return (
      <div className="ar-card ar-card--granted" key={request.requestId}>
        {this.renderWho(request, true)}

        <div className="ar-line">
          Has {MODE_LABEL[request.decisionMode] || 'access'} at{' '}
          <strong>{request.locationName}</strong>
          {request.targetCompanyName ? (
            <>
              {' '}
              for <strong>{request.targetCompanyName}</strong>
            </>
          ) : null}
          .
        </div>

        {confirm ? (
          this.renderConfirm(confirm, request)
        ) : (
          <>
            <div className="ar-meta">
              {request.autoApproved
                ? 'Granted by NuVe System'
                : `Approved by ${request.decidedByName || 'an owner'}${
                    request.decidedByUsername
                      ? ` (${request.decidedByUsername})`
                      : ''
                  }`}
              {timeAgo(request.decidedAt) ? (
                <>
                  <span className="ar-meta__dot">&#183;</span>
                  {timeAgo(request.decidedAt)}
                </>
              ) : null}
            </div>
            <WriteGuard>
              <div className="ar-actions">
                <button
                  type="button"
                  className="ar-btn ar-btn--danger"
                  onClick={() => this.ask('revoke', request)}
                >
                  Revoke access
                </button>
              </div>
            </WriteGuard>
          </>
        )}
      </div>
    );
  };

  renderOutgoing = (request) => {
    const confirm = this.pendingConfirm(request);
    return (
      <div className="ar-card ar-card--outgoing" key={request.requestId}>
        {this.renderWho(request)}

        <div className="ar-line">
          Waiting for an owner of <strong>{request.locationName}</strong> to
          approve.
        </div>

        {this.renderChange(request.currentMode, request.requestedMode)}

        {confirm ? (
          this.renderConfirm(confirm, request)
        ) : (
          <>
            <div className="ar-meta">
              Sent {timeAgo(request.createdAt) || 'recently'}
            </div>
            <WriteGuard>
              <div className="ar-actions">
                <button
                  type="button"
                  className="ar-btn ar-btn--ghost"
                  onClick={() => this.ask('cancel', request)}
                >
                  Withdraw request
                </button>
              </div>
            </WriteGuard>
          </>
        )}
      </div>
    );
  };

  renderSection = (label, count) => (
    <div className="ar-section">
      <span className="ar-section__label">{label}</span>
      <span className="ar-section__count">{count}</span>
      <span className="ar-section__rule" />
    </div>
  );

  render() {
    const incoming = this.props.incomingRequests || [];
    const outgoing = this.props.outgoingRequests || [];
    // Only APPROVED rows are revocable. Declined and withdrawn history is
    // deliberately left out — it carries no action and would bury the
    // rows that do.
    const granted = (this.props.incomingDecided || []).filter(
      (r) => r.status === 'APPROVED'
    );

    if (!incoming.length && !outgoing.length && !granted.length) {
      return (
        <div className="ar-empty">
          <div className="ar-empty__badge">&#10003;</div>
          <div className="ar-empty__title">Nothing to review</div>
          <div className="ar-empty__text">
            Requests to reach this location show up here, along with anyone
            from another company who already has access.
          </div>
        </div>
      );
    }

    return (
      <div className="ar-panel">
        {incoming.length ? (
          <>
            {this.renderSection('Needs your approval', incoming.length)}
            {incoming.map(this.renderIncoming)}
          </>
        ) : null}

        {granted.length ? (
          <>
            {this.renderSection('Active access', granted.length)}
            {granted.map(this.renderGranted)}
          </>
        ) : null}

        {outgoing.length ? (
          <>
            {this.renderSection('Your requests', outgoing.length)}
            {outgoing.map(this.renderOutgoing)}
          </>
        ) : null}
      </div>
    );
  }
}

AccessRequestsPanel.propTypes = {
  incomingRequests: PropTypes.array,
  incomingDecided: PropTypes.array,
  outgoingRequests: PropTypes.array,
  decideAccessRequest: PropTypes.func,
  locationId: PropTypes.string,
  companyId: PropTypes.string,
};

AccessRequestsPanel.defaultProps = {
  incomingRequests: [],
  incomingDecided: [],
  outgoingRequests: [],
  decideAccessRequest: () => {},
};

export default AccessRequestsPanel;
